// src/context/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface AuthUser {
  _id: string;
  email: string;
  role: string;
  avatar: string | null; // ALWAYS relative path like /uploads/avatars/123.png
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  signup: (
    email: string,
    password: string,
    accessCode: string
  ) => Promise<boolean>;
  logout: () => void;
  updateRole: (role: string) => void;
  updateAvatarURL: (avatarPath: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ------------------------------
// Environment API Base
// ------------------------------
const API_BASE = process.env.REACT_APP_API_URL || "";

// Convert any absolute URL -> relative path (universal across environments)
const toRelativePath = (url: string | null): string | null => {
  if (!url) return null;
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.startsWith("/") ? url : null;
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // ------------------------------
  // Load user from localStorage
  // ------------------------------
  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    const savedToken = localStorage.getItem("token");

    if (savedUser && savedToken) {
      const parsed = JSON.parse(savedUser);

      // Normalize avatar to relative path
      parsed.avatar = toRelativePath(parsed.avatar);

      setUser(parsed);
      setToken(savedToken);
    }

    setLoading(false);
  }, []);

  // ------------------------------
  // LOGIN
  // ------------------------------
  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) return false;

      const data = await res.json();

      // FIX: Backend may return any of these
      const rawAvatar =
        data.user.avatar || data.user.avatarUrl || data.user.avatarFull || null;

      const authenticatedUser: AuthUser = {
        _id: data.user._id,
        email: data.user.email,
        role: data.user.role,
        avatar: toRelativePath(rawAvatar),
      };

      setUser(authenticatedUser);
      setToken(data.token);

      localStorage.setItem("user", JSON.stringify(authenticatedUser));
      localStorage.setItem("token", data.token);

      return true;
    } catch (err) {
      console.error("Login error:", err);
      return false;
    }
  };

  // ------------------------------
  // SIGNUP
  // ------------------------------
  const signup = async (
    email: string,
    password: string,
    accessCode: string
  ): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, accessCode }),
      });

      return res.ok;
    } catch (err) {
      console.error("Signup error:", err);
      return false;
    }
  };

  // ------------------------------
  // LOGOUT
  // ------------------------------
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  };

  // ------------------------------
  // UPDATE ROLE
  // ------------------------------
  const updateRole = (role: string) => {
    if (!user) return;

    const updated = { ...user, role };
    setUser(updated);
    localStorage.setItem("user", JSON.stringify(updated));
  };

  // ------------------------------
  // UPDATE AVATAR
  // ------------------------------
  const updateAvatarURL = (avatarPath: string | null) => {
    if (!user) return;

    const updated = { ...user, avatar: avatarPath };
    setUser(updated);
    localStorage.setItem("user", JSON.stringify(updated));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        signup,
        logout,
        updateRole,
        updateAvatarURL,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
